import { locationStatus$ } from './geo/geo-location-signals'
import { ClientConfig } from '../config/ClientConfig'
import { LogManager } from '../LogManager'
import { createDebugLogger } from 'shared/debug'

const debug = createDebugLogger('geo:protection')
import { withTimeout } from '../script-utils/withTimeout'
import { withRetry, type RetryStrategy } from '../script-utils/withRetry'
import { fetchGeoComplyToken } from '../communication/customer/CustomerDueDiligenceApi'
import {
	hydrateFromResult,
	clearGeoLocationState,
	getLatestLocationResult,
	getLocationToken,
	hasValidLocation,
	getSubdivision,
	extractStateFromSubdivision,
	getGeoLocationState,
	IDLE_STATE,
	GEO_STATUS_FAILED,
} from './geo/geo-location-state'
import { type GeoFailureReason, type GeoLocationResult } from './geo/geo-failure-reason'
import { GeoFailureCategory } from './geo/geo-failure-category'
import { GeoLocationSdkLoadError, GeoLocationTimeoutError, GeoLocationTokenError, GEO_TIMEOUT_PHASE } from './geo/geo-location-errors'
import { getFaultInjector, installFaultInjector, createFaultInjector, uninstallFaultInjector } from './geo/geo-fault-injector'
import { inferReasonFromError } from './geo/geo-reason-inference'
import { locationUpdateSequence } from './geo/location-update-sequence'
import { createGeoResultStorage } from './geo/geo-result-storage'
import { injectGeoPaymentModalStyles } from './geo/geo-payment-modal-styles'
import {
	GEO_LOCATION_UPDATE,
	isGeoToolingAllowlisted,
	synthesiseLegacySpoofSequence,
	getSpoofSequence,
} from './geo/geo-spoof-helpers'
import { appendGeoGateLog } from './geo/geo-gate-debug-storage'
import { GeoConfig } from './geo/GeoConfig'
import { AuthService } from '../auth/AuthService'
import { createFakeSdk, seedQueueFromSteps } from './geo/spoof/geo-fake-sdk'
import { subdivisionToCountry } from './geo/geo-subdivision-utils'

const BRAND = 'POK'

function successResult(subdivision: string): Extract<LocateDeviceResult, { status: 'Success' }> {
	return { status: 'Success', subdivision, country: subdivisionToCountry(subdivision), token: '' }
}

function failedResult(reason?: GeoFailureReason): GeoLocationResult {
	return { status: GEO_STATUS_FAILED, token: '', ...(reason && { reason }) }
}

let cachedSdkPromise: Promise<void> | undefined
let cachedLocationPromise: Promise<GeoLocationResult> | undefined

function persistToSessionStorage(result: GeoLocationResult): void {
	try {
		createGeoResultStorage(window.sessionStorage).write(result)
	} catch {
		// sessionStorage may be unavailable (private browsing, storage quota)
	}
}

let persistenceListener: ((event: Event) => void) | undefined

export function registerGeoPersistenceListener(): void {
	if (persistenceListener) return
	persistenceListener = (event: Event) => {
		const result = (event as CustomEvent<LocateDeviceResult>).detail
		persistToSessionStorage(result)
	}
	window.addEventListener(GEO_LOCATION_UPDATE, persistenceListener)
}

function dispatchLocationEvent(result: LocateDeviceResult): void {
	window.dispatchEvent(new CustomEvent(GEO_LOCATION_UPDATE, { detail: result }))
}


const SDK_LOAD_MAX_ATTEMPTS = 3

function attemptSdkLoad(): { promise: Promise<void>; script: HTMLScriptElement } {
	const { GEOCOMPLY_SDK_URL } = ClientConfig
	const script = document.createElement('script')
	const promise = new Promise<void>((resolve, reject) => {
		script.addEventListener('load', () => {
			injectGeoPaymentModalStyles()
			resolve()
		})
		script.addEventListener('error', () => {
			reject(new GeoLocationSdkLoadError())
		})
		script.type = 'text/javascript'
		script.async = false
		script.src = GEOCOMPLY_SDK_URL
		document.head.appendChild(script)
	})
	return { promise, script }
}

const sdkLoadRetryStrategy: RetryStrategy = {
	maxAttempts: SDK_LOAD_MAX_ATTEMPTS,
	shouldRetry: (err) => err instanceof GeoLocationSdkLoadError,
	onTerminalFailure: () => LogManager.error({ message: 'GeoLocation SDK failed to load' }),
}

const loadGeoLocationSdk = (): Promise<void> => {
	const sdkLoadFault = getFaultInjector()?.nextSdkLoadFault() ?? null
	if (sdkLoadFault) {
		return Promise.reject(sdkLoadFault)
	}
	if (getFaultInjector()?.nextSdkLoadTimeoutFault()) {
		return Promise.reject(new GeoLocationTimeoutError('GeoLocation SDK load timeout', GEO_TIMEOUT_PHASE.SDK_LOAD))
	}
	if (getFaultInjector()?.nextSdkLoadTokenFault()) {
		return Promise.reject(new GeoLocationTokenError())
	}

	if (cachedSdkPromise) return cachedSdkPromise

	const attemptLoad = async (): Promise<void> => {
		const { promise, script } = attemptSdkLoad()
		try {
			await promise
		} catch (err) {
			document.head.removeChild(script)
			throw err
		}
	}

	cachedSdkPromise = withTimeout(
		withRetry(sdkLoadRetryStrategy, attemptLoad),
		GeoConfig.sdkLoadTimeoutMs(),
		'GeoLocation SDK load timeout',
	)

	return cachedSdkPromise
}

function applyLocationResult(result: LocateDeviceResult): void {
	locationUpdateSequence.invalidate()
	hydrateFromResult(result)
	dispatchLocationEvent(result)
}

const handleLocationUpdate = (rawResult: LocateDeviceResult): void => {
	if (!cachedLocationPromise) {
		debug('handleLocationUpdate: cachedLocationPromise is null - callback dropped')
		return
	}
	debug(`handleLocationUpdate: status:${rawResult.status}`)
	applyLocationResult(rawResult)
}

function buildBaseLocateDeviceParams(accountId: string): LocateDeviceParams {
	return {
		brand: BRAND,
		accountId,
		getAccessToken: (() => {
			let callCount = 0
			return () => {
				callCount++
				appendGeoGateLog('sdk-call', `PAY token fetch #${callCount}`)
				const tokenFault = getFaultInjector()?.nextTokenFault() ?? null
				if (tokenFault) return Promise.reject(tokenFault)
				return fetchGeoComplyToken()
			}
		})(),
		callback: handleLocationUpdate,
	}
}

async function executeLocateDevice(params: LocateDeviceParams): Promise<LocateDeviceResult> {
	const locateDevicePromise = getFaultInjector()?.nextLocateTimeoutFault()
		? new Promise<never>(() => {}) // never resolves; withTimeout fires the timeout
		: window.rmp.locateDevice(params)
	const result = await withTimeout(locateDevicePromise, GeoConfig.locateDeviceTimeoutMs(), 'locateDevice timeout')
	const failedResult = result.status === 'Failed' ? result as Extract<LocateDeviceResult, { status: 'Failed' }> : null
	appendGeoGateLog('sdk-call', `locateDevice resolved - status: ${result.status}${failedResult ? `, failureCategory: ${failedResult.failureCategory ?? 'none'}, troubleshooting: ${failedResult.troubleshooting ? 'present' : 'absent'}` : ''}`)
	return result
}


export async function retryResolveGeoLocation(options: { userId: string }): Promise<LocateDeviceResult> {
	const accountId = 'POK-' + options.userId
	const params: LocateDeviceParams = {
		...buildBaseLocateDeviceParams(accountId),
		...(GeoConfig.customGuidanceEnabled() ? { defaultGuidanceBehavior: 'custom' as const } : {}),
	}
	return executeLocateDevice(params)
}

export function restartMonitoring(
	options: { userId: string },
	execute: (params: LocateDeviceParams) => Promise<LocateDeviceResult> = executeLocateDevice,
): Promise<LocateDeviceResult> {
	const accountId = 'POK-' + options.userId
	// showPreHint is intentionally omitted: the permission explainer is not shown again after a restart.
	const params: LocateDeviceParams = {
		...buildBaseLocateDeviceParams(accountId),
		...(GeoConfig.customGuidanceEnabled() ? { defaultGuidanceBehavior: 'custom' as const } : {}),
	}
	debug(`restartMonitoring: calling locateDevice cachedLocationPromise=${!!cachedLocationPromise}`)
	return execute(params).then(result => {
		debug(`restartMonitoring: locateDevice resolved status:${result.status}`)
		// The first result of a restarted loop resolves this promise without firing the callback.
		// Route it through handleLocationUpdate so a failure can show guidance and a pass marks
		// the location verified again.
		handleLocationUpdate(result)
		return result
	}).catch(err => {
		debug(`restartMonitoring: locateDevice rejected: ${err?.message ?? String(err)}`)
		throw err
	})
}

export function simulateMonitoringSuccess(subdivision: string): void {
	applyLocationResult(successResult(subdivision))
}

export const resolveGeoLocation = (options: {
	getAccountId: () => Promise<string>
	userId: string
	email?: string
	isProtected?: () => boolean
}): Promise<GeoLocationResult> => {
	if (cachedLocationPromise) return cachedLocationPromise

	cachedLocationPromise = new Promise((resolve) => {
		// Resolve from first dispatched event
		let resolved = false
		const onFirst = (e: Event): void => {
			window.removeEventListener(GEO_LOCATION_UPDATE, onFirst)
			resolved = true
			resolve((e as CustomEvent<GeoLocationResult>).detail)
		}
		window.addEventListener(GEO_LOCATION_UPDATE, onFirst)

		// Install fake SDK when allowlisted AND either:
		// (a) a sequence is explicitly configured (guidance ON flow), or
		// (b) legacy spoof cookies are set and guidance is OFF. In that case synthesise a
		//     sequence step from GEOCOMPLY_SPOOF_STATUS so the existing fake SDK can be used.
		// Without either, fall through to the real SDK so PAY tool overrides take effect.
		if (isGeoToolingAllowlisted(options.userId, options.email)) {
			if (getSpoofSequence() === null && !GeoConfig.customGuidanceEnabled()) {
				synthesiseLegacySpoofSequence()
			}
			if (getSpoofSequence() !== null) {
				appendGeoGateLog('spoof', 'Fake SDK installed, all spoof paths driven by sequence')
				installFaultInjector(createFaultInjector())
				;(window as any).__geoFaultInjector = getFaultInjector()
				const fakeSdk = createFakeSdk()
				const steps = getSpoofSequence() ?? []
				seedQueueFromSteps(steps, (entry) => {
					if (entry.type === 'result') fakeSdk.controller.queueResult(entry.result, entry.opts)
					else if (entry.type === 'hint') fakeSdk.controller.queueHint(entry.guidance)
				})
				;(window as any).rmp = {
					locateDevice: fakeSdk.locateDevice,
					stopLocatingDevice: fakeSdk.controller.stop,
				}
				;(window as any).__geoSdkController = fakeSdk.controller
				cachedSdkPromise = Promise.resolve()
			} else {
				appendGeoGateLog('sdk-call', 'SDK loading, real locate starting')
			}
		} else {
			appendGeoGateLog('sdk-call', 'SDK loading, real locate starting')
		}

		loadGeoLocationSdk()
			.then(async () => {
				const accountId = await options.getAccountId()
				const params = buildBaseLocateDeviceParams(accountId)
				let retryableContext: { current: 'login' | 'monitor' } | null = null
				if (GeoConfig.customGuidanceEnabled()) {
					const [{ showGuidanceModal, GuidanceIntent, GuidanceButtonType, GuidanceModalType, GuidanceAnalyticsContext, ModalResultOutcome }, { emitGeoEvent, GeoEvent }] = await Promise.all([
						import('./geo/geo-guidance-modal'),
						import('./geo/geo-analytics'),
					])
					params.defaultGuidanceBehavior = 'custom'
					retryableContext = { current: GuidanceAnalyticsContext.LOGIN }
					params.showPreHint = async (guidance) => {
						emitGeoEvent(GeoEvent.GUIDANCE_SHOWN, {
							context: GuidanceAnalyticsContext.LOGIN,
							modal: GuidanceModalType.HINT,
							failureCategory: null,
						})
						const result = await showGuidanceModal(guidance, [
							{ label: i18n.t('geo-guidance.buttons.gotIt'), type: GuidanceButtonType.PRIMARY, intent: GuidanceIntent.RETRY },
						], { timeoutMs: GeoConfig.modalTimeoutMs() })
						const outcomeAction = result.outcome === ModalResultOutcome.ACTION ? result.action.intent : result.outcome
						const outcomeLabel = result.outcome === ModalResultOutcome.ACTION ? result.action.label : ''
						emitGeoEvent(GeoEvent.GUIDANCE_OUTCOME, {
							context: GuidanceAnalyticsContext.LOGIN,
							modal: GuidanceModalType.HINT,
							failureCategory: null,
							action: outcomeAction,
							actionLabel: outcomeLabel,
						})
						const shouldSignOut =
							result.outcome === ModalResultOutcome.DISMISSED ||
							result.outcome === ModalResultOutcome.TIMED_OUT
						if (shouldSignOut) {
							void AuthService.logout(window.location.origin + '/user/enable-location')
						}
					}
					// SDK only calls showRetryableFailure for integrity failures:
					// https://github.com/VGW/pay-rmp/blob/main/src/domain/geolocation/sdk/index.ts#L213
					params.showRetryableFailure = async (guidance) => {
						debug(`showRetryableFailure: called - isProtected=${options.isProtected?.() ?? false}`)
						if (options.isProtected?.()) {
							debug('showRetryableFailure: protection active, reporting the failure instead of retrying')
							locationStatus$.next('unverified')
							// Throw to take the SDK catch path: the failure is reported like any other and
							// monitoring restarts when protection ends.
							throw new Error('retryable failure during gameplay protection')
						}
						emitGeoEvent(GeoEvent.GUIDANCE_SHOWN, {
							context: retryableContext.current,
							modal: GuidanceModalType.RETRYABLE,
							failureCategory: GeoFailureCategory.INTEGRITY,
						})
						const actions = [
							{ label: i18n.t('geo-guidance.buttons.tryAgain'), type: GuidanceButtonType.PRIMARY, intent: GuidanceIntent.RETRY },
							...(guidance.faqUrl ? [{ label: i18n.t('geo-guidance.buttons.needMoreHelp'), type: GuidanceButtonType.SECONDARY, intent: GuidanceIntent.NAVIGATE, href: guidance.faqUrl }] : []),
							{ label: i18n.t('geo-guidance.buttons.logOut'), type: GuidanceButtonType.TERTIARY, intent: GuidanceIntent.LOGOUT },
						]
						const result = await showGuidanceModal(guidance, actions, {
							timeoutMs: GeoConfig.modalTimeoutMs(),
							onNavigate: (action) => {
								emitGeoEvent(GeoEvent.GUIDANCE_OUTCOME, {
									context: retryableContext.current,
									modal: GuidanceModalType.RETRYABLE,
									failureCategory: GeoFailureCategory.INTEGRITY,
									action: GuidanceIntent.NAVIGATE,
									actionLabel: action.label,
									actionHref: action.href,
								})
							},
						})
						const outcomeAction = result.outcome === ModalResultOutcome.ACTION ? result.action.intent : result.outcome
						const outcomeLabel = result.outcome === ModalResultOutcome.ACTION ? result.action.label : ''
						emitGeoEvent(GeoEvent.GUIDANCE_OUTCOME, {
							context: retryableContext.current,
							modal: GuidanceModalType.RETRYABLE,
							failureCategory: GeoFailureCategory.INTEGRITY,
							action: outcomeAction,
							actionLabel: outcomeLabel,
						})
						const shouldSignOut =
							result.outcome === ModalResultOutcome.DISMISSED ||
							result.outcome === ModalResultOutcome.TIMED_OUT ||
							(result.outcome === ModalResultOutcome.ACTION && result.action.intent === GuidanceIntent.LOGOUT)
						if (shouldSignOut) {
							void AuthService.logout(window.location.origin + '/user/enable-location')
							// Throw to take the SDK catch path: prevents the SDK from
							// continuing the locate loop while sign-out is pending.
							throw new Error('player exited retryable modal')
						}
					}
				}
				const result = await executeLocateDevice(params)
				if (retryableContext) {
					retryableContext.current = 'monitor'
				}
				// Fallback: if SDK resolved without firing the callback, process the result directly
				if (!resolved) {
					handleLocationUpdate(result)
				}
			})
			.catch((err) => {
				if (!(err instanceof GeoLocationSdkLoadError)) LogManager.error({ message: err?.message ?? 'GeoLocation failed', error: err })
				const reason = inferReasonFromError(err)
				const result = failedResult(reason)
				hydrateFromResult(result)
				dispatchLocationEvent(result)
			})
	})

	return cachedLocationPromise
}

const clearCachedSdk = (): void => {
	cachedSdkPromise = undefined
	uninstallFaultInjector()
}

export const clearCachedLocationState = (): void => {
	cachedLocationPromise = undefined
	locationUpdateSequence.invalidate()
	clearGeoLocationState()
	if (persistenceListener) {
		window.removeEventListener(GEO_LOCATION_UPDATE, persistenceListener)
		persistenceListener = undefined
	}
	try {
		createGeoResultStorage(window.sessionStorage).clear()
	} catch {
		// sessionStorage may be unavailable
	}
}

export const clearCachedLocation = (): void => {
	clearCachedSdk()
	clearCachedLocationState()
}

export function getGeoHeaders(): { 'X-Geo-Subdivision': string } | undefined {
	return hasValidLocation() ? { 'X-Geo-Subdivision': getSubdivision() } : undefined
}

export {
	hydrateFromResult,
	applyLocationResult,
	getLatestLocationResult,
	getLocationToken,
	hasValidLocation,
	getSubdivision,
	extractStateFromSubdivision,
	getGeoLocationState,
	IDLE_STATE,
}
